import { useEffect, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { Card, Line } from 'ktm-ui';
import toastr from "toastr";
import axios, { AxiosError } from "axios";
import Select from 'react-select';
import '../styles/Advisors.css';

const PROVINCE_OPTIONS = [
  { value: "AB", label: "Alberta" },
  { value: "BC", label: "British Columbia" },
  { value: "MB", label: "Manitoba" },
  { value: "NB", label: "New Brunswick" },
  { value: "NL", label: "Newfoundland and Labrador" },
  { value: "NS", label: "Nova Scotia" },
  { value: "NT", label: "Northwest Territories" },
  { value: "NU", label: "Nunavut" },
  { value: "ON", label: "Ontario" },
  { value: "PE", label: "Prince Edward Island" },
  { value: "QC", label: "Quebec" },
  { value: "SK", label: "Saskatchewan" },
  { value: "YT", label: "Yukon" },
];

const listOptions = (list) => (list ?? []).map((item) => ({
    value: `${item.id}`,
    label: `${item.healthClaimAccountId} - ${item.name}`,
}));

const resolveDirector = (list, id) => {
    if (!id) return "";
    const match = listOptions(list).find(opt => opt.value.toLowerCase() === String(id).toLowerCase());
    return match ? match.value : String(id);
};

const directorsForProvince = (provinceDirectors, rmdList, bddList, province) => {
    const row = provinceDirectors?.[province];
    return {
        rmd: resolveDirector(rmdList, row?.rmd),
        bdd1: resolveDirector(bddList, row?.bdd1),
        bdd2: resolveDirector(bddList, row?.bdd2),
    };
};

const provinceSuppliesDirectors = (provinceDirectors, province) => {
    const row = provinceDirectors?.[province];
    return Boolean(row?.rmd || row?.bdd1 || row?.bdd2);
};

const directorOptions = (list, distroList, currentId) => {
    const options = listOptions(list);
    const id = String(currentId ?? "");

    if (!id || options.some(opt => opt.value.toLowerCase() === id.toLowerCase())) return options;

    const distro = (distroList ?? []).find(d => String(d.id).toLowerCase() === id.toLowerCase());
    if (!distro) return options;

    return [...options, { value: `${distro.id}`, label: `${distro.healthClaimAccountId} - ${distro.name}` }];
};

export const AddAdvisorModal = ({ advisors, distroList, rmdList, bddList, provinceDirectors, showModal, onHide }) => {
    const [message, setMessage] = useState(null);
    const [confirmed, setConfirmed] = useState(false);
    const [formData, setFormData] = useState({
        lastName: "",
        firstName: "",
        phone: "",
        email: "",
        a1Number: "",
        rmd: "",
        bdd1: "",
        bdd2: "",
        distroId: "",
        healthClaimRelationship: "",
        isOwner: false,
        enableScheduledCredits: false,
        isNationalDistributionPartner: false,
        mailingProv: ""
    });

    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value,
        });
    };

    const distroIdOf = (composite) => String(composite ?? "").split("|")[1] ?? "";

    const distroFor = (composite) => {
        const id = distroIdOf(composite).toLowerCase();
        return distroList.find(d => String(d.id).toLowerCase() === id);
    };

    const isJgAdvisor = Boolean(distroFor(formData?.distroId)?.isJg);

    const directorsFromProvince = isJgAdvisor && provinceSuppliesDirectors(provinceDirectors, formData?.mailingProv);

    const directorsLocked = !isJgAdvisor || directorsFromProvince;

    const handleProvinceChange = (e) => {
        const province = e.target.value;

        if (!isJgAdvisor || !provinceDirectors) {
            setFormData({ ...formData, mailingProv: province });
            return;
        }

        setFormData({ ...formData, mailingProv: province, ...directorsForProvince(provinceDirectors, rmdList, bddList, province) });
    };

    const handleDistroChange = (value) => {
        if (!distroFor(value)?.isJg) {
            setFormData({ ...formData, distroId: value, rmd: "", bdd1: "", bdd2: "" });
            return;
        }

        if (!provinceDirectors || !formData?.mailingProv) {
            setFormData({ ...formData, distroId: value });
            return;
        }

        setFormData({ ...formData, distroId: value, ...directorsForProvince(provinceDirectors, rmdList, bddList, formData.mailingProv) });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            if (!confirmed) {
                const response = await axios.get("/api/Advisors/validateadvisoraccount", {
                    params: { email: formData.email }
                });

                if (response.data.otherAccountExisting) {
                    setConfirmed(true);
                    setMessage("This advisor email is linked to the same user account as an Employee and/or Plan Admin. Existing access may be affected.");
                    return;
                }
            }

            await axios.post("/api/Advisors/createadvisor", formData);
            toastr.success("Successfully created an advisor!");
            setFormData({
                lastName: "",
                firstName: "",
                phone: "",
                email: "",
                a1Number: "",
                rmd: "",
                bdd1: "",
                bdd2: "",
                distroId: "",
                healthClaimRelationship: "",
                isOwner: false,
                enableScheduledCredits: false,
                isNationalDistributionPartner: false,
                mailingProv: "",
            });
            setMessage(null);
            onHide();
        }
        catch (e) {
            const err = e as AxiosError;
            if (err.response) {
                toastr.error("Failed to create an advisor", err.response?.data)
            }
        }
    }

    const distroOptions = distroList.map((distro) => ({
        value: `${distro.healthClaimAccountId}|${distro.id}`,
        label: `${distro.healthClaimAccountId} - ${distro.name}`,
    }));

    return (
        <Modal show={showModal} onHide={onHide} className="Account-Details-Modal">
            <Form as="form" onSubmit={handleSubmit}>
                <Modal.Header closeButton>
                    <Modal.Title className="advisor-modal-title">Add Advisor</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Card>
                        {message && <Form.Text className="text-danger">{message}</Form.Text>}
                        <Form.Group>
                            <Form.Label className="advisor-form-label">Choose Firm<span style={{ color: 'red' }}>*</span></Form.Label>
                            <Select
                                name="distroId"
                                options={distroOptions}
                                value={distroOptions.find(opt => opt.value === formData?.distroId)}
                                onChange={(selectedOption) => handleDistroChange(selectedOption?.value ?? "")}
                                isSearchable
                                isClearable
                                placeholder="Choose Firm"
                            />
                        </Form.Group>
                        <Form.Group as={Row}>
                            <Col>
                                <Form.Label className="advisor-form-label">Last Name<span style={{ color: 'red' }}>*</span></Form.Label>
                                <Form.Control name="lastName" value={formData?.lastName} onChange={handleChange} required />
                            </Col>
                            <Col>
                                <Form.Label className="advisor-form-label">First Name<span style={{ color: 'red' }}>*</span></Form.Label>
                                <Form.Control name="firstName" value={formData?.firstName} onChange={handleChange} required />
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row}>
                            <Col>
                                <Form.Label className="advisor-form-label">Phone</Form.Label>
                                <Form.Control type="tel" name="phone" value={formData?.phone} onChange={handleChange} />
                            </Col>
                            <Col>
                                <Form.Label className="advisor-form-label">Email<span style={{ color: 'red' }}>*</span></Form.Label>
                                <Form.Control type="email" name="email" value={formData?.email} onChange={handleChange} required />
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row}>
                            <Col>
                                <Form.Label className="advisor-form-label">Province<span style={{ color: 'red' }}>*</span></Form.Label>
                                <Form.Select name="mailingProv" value={formData?.mailingProv} onChange={handleProvinceChange} required>
                                    <option value="">Select Province</option>
                                    {PROVINCE_OPTIONS.map((prov) => (
                                        <option key={prov.value} value={prov.value}>{prov.label}</option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col>
                                <Form.Label className="advisor-form-label">Relationship</Form.Label>
                                <Form.Control type="healthClaimRelationship" name="healthClaimRelationship" value={formData?.healthClaimRelationship} onChange={handleChange} />
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row}>
                            <Col>
                                <Form.Label className="advisor-form-label">A1 Number (JG Advisor Only)</Form.Label>
                                <Form.Control type="number" name="a1Number" value={formData?.a1Number} onChange={handleChange} />
                            </Col>
                            <Col>
                                <Form.Label className="advisor-form-label">RMD (JG Advisor Only)</Form.Label>
                                <Form.Select name="rmd" value={formData?.rmd || ""} onChange={handleChange} disabled={directorsLocked}>
                                    <option value="">None</option>
                                    {directorOptions(rmdList, distroList, formData?.rmd).map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </Form.Select>
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row}>
                            <Col>
                                <Form.Label className="advisor-form-label">BDD1 (JG Advisor Only)</Form.Label>
                                <Form.Select name="bdd1" value={formData?.bdd1 || ""} onChange={handleChange} disabled={directorsLocked}>
                                    <option value="">None</option>
                                    {directorOptions(bddList, distroList, formData?.bdd1).map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col>
                                <Form.Label className="advisor-form-label">BDD2 (JG Advisor Only)</Form.Label>
                                <Form.Select name="bdd2" value={formData?.bdd2 || ""} onChange={handleChange} disabled={directorsLocked}>
                                    <option value="">None</option>
                                    {directorOptions(bddList, distroList, formData?.bdd2).map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </Form.Select>
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row}>
                            <Col>
                                <Form.Label className="advisor-form-label">Is Owner</Form.Label>
                                <div className="form-check form-switch">
                                    <input type="checkbox" className="form-check-input" name="isOwner" checked={formData?.isOwner} onChange={handleChange} />
                                </div>
                            </Col>
                            <Col>
                                <Form.Label className="advisor-form-label">National Distribution Partner</Form.Label>
                                <div className="form-check form-switch">
                                    <input type="checkbox" className="form-check-input" name="isNationalDistributionPartner" checked={formData?.isNationalDistributionPartner} onChange={handleChange} />
                                </div>
                            </Col>
                        </Form.Group>
                    </Card>
                </Modal.Body>
                <Modal.Footer>
                    <Button type="submit" variant="primary">
                        Submit
                    </Button>
                    <Button type="button" variant="secondary" onClick={onHide}>
                        Close
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};


export const AdvisorDetailsModal = ({ selectedItem, showModal, onHide, rmdList, bddList, distroList, advisorList, provinceDirectors }) => {
    const [message, setMessage] = useState(null);
    const [confirmed, setConfirmed] = useState(false);
    const [isEditable, setIsEditable] = useState(false);
    const [formData, setFormData] = useState({
        id: "",
        distroAccountId: "",
        lastName: "",
        firstName: "",
        healthClaimAdvisorId: "",
        healthClaimAccountId: "",
        phone: "",
        email: "",
        a1number: "",
        rmd: "",
        bdd1: "",
        bdd2: "",
        healthClaimRelationship: "",
        userId: "",
        isOwner: false,
        isActive: false,
        enableScheduledCredits: false,
        isNationalDistributionPartner: false,
        mailingProv: "",
    });

    useEffect(() => {
        if (showModal && selectedItem) {
            setFormData({
                id: selectedItem?.id,
                distroAccountId: selectedItem?.distroAccountId,
                lastName: selectedItem?.lastName,
                firstName: selectedItem?.firstName,
                healthClaimAdvisorId: selectedItem?.healthClaimAdvisorId,
                healthClaimAccountId: selectedItem?.healthClaimAccountId,
                phone: selectedItem?.phone,
                email: selectedItem?.email,
                isActive: selectedItem?.isActive,
                isOwner: selectedItem?.isOwner,
                a1number: selectedItem?.a1number,
                rmd: selectedItem?.rmd1DistroId,
                bdd1: selectedItem?.bdd1DistroId,
                bdd2: selectedItem?.bdd2DistroId,
                healthClaimRelationship: selectedItem?.healthClaimRelationship,
                userId: selectedItem?.userId,
                enableScheduledCredits: selectedItem?.enableScheduledCredits,
                isNationalDistributionPartner: selectedItem?.isNationalDistributionPartner ?? false,
                mailingProv: selectedItem?.mailingProv,
            });
        }
    }, [showModal]);

    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value,
        });
    };

    const handleListChange = (selectedOption, actionMeta) => {
        const { name } = actionMeta;
        const value = selectedOption ? selectedOption.value : '';

        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const distroFor = (id) => distroList.find(d => String(d.id).toLowerCase() === String(id ?? "").toLowerCase());

    const isJgAdvisor = Boolean(distroFor(formData?.distroAccountId)?.isJg);

    const directorsFromProvince = isJgAdvisor && provinceSuppliesDirectors(provinceDirectors, formData?.mailingProv);

    const directorsLocked = !isJgAdvisor || directorsFromProvince;

    const handleProvinceChange = (e) => {
        const province = e.target.value;

        if (!isJgAdvisor || !provinceDirectors) {
            setFormData({ ...formData, mailingProv: province });
            return;
        }

        setFormData({ ...formData, mailingProv: province, ...directorsForProvince(provinceDirectors, rmdList, bddList, province) });
    };

    const handleDistroChange = (selectedOption, actionMeta) => {
        const value = selectedOption ? selectedOption.value : '';

        if (!distroFor(value)?.isJg) {
            setFormData({ ...formData, distroAccountId: value, rmd: "", bdd1: "", bdd2: "" });
            return;
        }

        if (!provinceDirectors || !formData?.mailingProv) {
            setFormData({ ...formData, distroAccountId: value });
            return;
        }

        setFormData({ ...formData, distroAccountId: value, ...directorsForProvince(provinceDirectors, rmdList, bddList, formData.mailingProv) });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            if (!confirmed) {
                const response = await axios.get("/api/Advisors/validateadvisoraccount", {
                    params: { email: formData.email }
                });

                if (response.data.otherAccountExisting) {
                    setConfirmed(true);
                    setMessage("This advisor email is linked to the same user account as an Employee and/or Plan Admin. Existing access may be affected.");
                    return;
                }
            }

            const response = await axios.put('/api/advisors/updateadvisor', formData);

            if (response.status === 200) {
                toastr.success('Successfully updated advisor details');
                setIsEditable(false);
                setMessage(null);
                onHide();
            } else {
                toastr.error(`Update failed: ${response.statusText}`);
            }
        } catch (e) {
            const err = e as AxiosError;
            if (err.response) {
                toastr.error("Failed to update the advisor details", err.response?.data)
            }
        }
    };

    const distroOptions = distroList.map((distro) => ({
        value: `${distro.id}`,
        label: `${distro.healthClaimAccountId} - ${distro.name}`,
    }));

    const selectedOption = (options, id) =>
        options.find(opt => opt.value.toLowerCase() === String(id ?? "").toLowerCase()) || "";

    const rmdOptions = directorOptions(rmdList, distroList, formData?.rmd);
    const bdd1Options = directorOptions(bddList, distroList, formData?.bdd1);
    const bdd2Options = directorOptions(bddList, distroList, formData?.bdd2);

    const toggleEdit = (e) => {
        e.preventDefault();
        setIsEditable(true);
    }

    if (!selectedItem) return null;

    return (
        <Modal show={showModal} onHide={onHide} className="Account-Details-Modal">
            <Form as="form" onSubmit={handleSubmit}>
                <Modal.Header closeButton>
                    <Modal.Title className="advisor-modal-title">Advisor Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Card>
                        {message && <Form.Text className="text-danger">{message}</Form.Text>}
                        <Form.Group>
                            <Form.Label className="advisor-form-label">Choose Firm</Form.Label>
                            <Select
                                name="distroAccountId"
                                options={distroOptions}
                                value={distroOptions.find(opt => opt.value === formData?.distroAccountId) || ""}
                                onChange={handleDistroChange}
                                isSearchable
                                isClearable
                                isDisabled={!isEditable}
                                placeholder="Choose Firm"
                            />
                        </Form.Group>
                        <Form.Group as={Row}>
                            <Col>
                                <Form.Label className="advisor-form-label">Health Claim Advisor ID</Form.Label>
                                <Form.Control
                                    name="healthClaimAdvisorId"
                                    value={formData?.healthClaimAdvisorId || ""}
                                    onChange={handleChange}
                                    readOnly />
                                <Form.Label className="RT-Static-Field__label">Relationship</Form.Label>
                                <Form.Control
                                    name="healthClaimRelationship"
                                    value={formData?.healthClaimRelationship}
                                    onChange={handleChange} 
                                    disabled={!isEditable} />
                            </Col>
                            <Col>
                                <Form.Label className="advisor-form-label">Is Active</Form.Label>
                                <div className="form-check form-switch">
                                    <input type="checkbox" className="form-check-input"
                                        disabled={!isEditable}
                                        name="isActive" checked={formData?.isActive} onChange={handleChange} />
                                </div>
                            </Col>
                            <Col>
                                <Form.Label className="advisor-form-label">Is Owner</Form.Label>
                                <div className="form-check form-switch">
                                    <input type="checkbox" className="form-check-input"
                                        disabled={!isEditable}
                                        name="isOwner" checked={formData?.isOwner} onChange={handleChange} />
                                </div>
                            </Col>
                            <Col>
                                <Form.Label className="advisor-form-label">Is JG</Form.Label>
                                <div className="form-check form-switch">
                                    <input type="checkbox" className="form-check-input"
                                        disabled
                                        name="isJg"
                                        checked={isJgAdvisor}
                                        readOnly />
                                </div>
                                <Form.Text className="text-muted">
                                    Set on the firm account
                                </Form.Text>
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row}>
                            <Col>
                                <Form.Label className="advisor-form-label">Last Name</Form.Label>
                                <Form.Control
                                    name="lastName"
                                    value={formData?.lastName || ""}
                                    onChange={handleChange}
                                    readOnly={!isEditable} />
                            </Col>
                            <Col>
                                <Form.Label className="advisor-form-label">First Name</Form.Label>
                                <Form.Control
                                    name="firstName"
                                    value={formData?.firstName || ""}
                                    onChange={handleChange}
                                    readOnly={!isEditable} />
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row}>
                            <Col>
                                <Form.Label className="advisor-form-label">Phone</Form.Label>
                                <Form.Control
                                    name="phone"
                                    value={formData?.phone || ""}
                                    onChange={handleChange}
                                    readOnly={!isEditable} />
                            </Col>
                            <Col>
                                <Form.Label className="advisor-form-label">Email</Form.Label>
                                <Form.Control
                                    name="email"
                                    value={formData?.email || ""}
                                    onChange={handleChange}
                                    readOnly={!isEditable} />
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row}>
                            <Col>
                                <Form.Label className="advisor-form-label">Province<span style={{ color: 'red' }}>*</span></Form.Label>
                                <Form.Select name="mailingProv" value={formData?.mailingProv || ""} onChange={handleProvinceChange} disabled={!isEditable} required>
                                    <option value="">Select Province</option>
                                    {PROVINCE_OPTIONS.map((prov) => (
                                        <option key={prov.value} value={prov.value}>{prov.label}</option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col>
                                <Form.Label className="advisor-form-label">National Distribution Partner</Form.Label>
                                <div className="form-check form-switch">
                                    <input type="checkbox" className="form-check-input"
                                        disabled={!isEditable}
                                        title="Sales split 20/80"
                                        name="isNationalDistributionPartner"
                                        checked={!!formData?.isNationalDistributionPartner}
                                        onChange={handleChange} />
                                </div>
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row}>
                            <Col>
                                <Form.Label className="advisor-form-label">A1 Number (JG Advisor Only)</Form.Label>
                                <Form.Control
                                    name="a1number"
                                    value={formData?.a1number || ""}
                                    onChange={handleChange}
                                    readOnly={!isEditable} />
                            </Col>
                            <Col>
                                <Form.Label className="advisor-form-label">RMD (JG Advisor Only)</Form.Label>
                                <Select
                                    name="rmd"
                                    options={rmdOptions}
                                    value={selectedOption(rmdOptions, formData?.rmd)}
                                    onChange={handleListChange}
                                    isSearchable
                                    isClearable
                                    isDisabled={!isEditable || directorsLocked}
                                    placeholder="Choose RMD"
                                />
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row}>
                            <Col>
                                <Form.Label className="advisor-form-label">BDD1 (JG Advisor Only)</Form.Label>
                                <Select
                                    name="bdd1"
                                    options={bdd1Options}
                                    value={selectedOption(bdd1Options, formData?.bdd1)}
                                    onChange={handleListChange}
                                    isSearchable
                                    isClearable
                                    isDisabled={!isEditable || directorsLocked}
                                    placeholder="Choose BDD1"
                                />
                            </Col>
                            <Col>
                                <Form.Label className="advisor-form-label">BDD2 (JG Advisor Only)</Form.Label>
                                <Select
                                    name="bdd2"
                                    options={bdd2Options}
                                    value={selectedOption(bdd2Options, formData?.bdd2)}
                                    onChange={handleListChange}
                                    isSearchable
                                    isClearable
                                    isDisabled={!isEditable || directorsLocked}
                                    placeholder="Choose BDD2"
                                />
                            </Col>
                        </Form.Group>
                    </Card>
                </Modal.Body>
                <Modal.Footer>
                    {!isEditable ? (
                        <Button variant="primary" type="button" onClick={toggleEdit}>
                            Edit
                        </Button>
                    ) : (
                        <>
                            <Button type="submit" variant="primary">
                                Save
                            </Button>
                            <Button type="button" variant="secondary" onClick={() => setIsEditable(false)}>
                                Cancel
                            </Button>
                        </>
                    )}
                </Modal.Footer>
            </Form>
        </Modal>
    );
};
